import { useCallback, useState } from "react";

/**
 * useState that survives leaving the page and coming back (browser Back, or the sidebar) — filters, view mode, sort and so on are kept in
 * sessionStorage for this tab, so Back lands on the page exactly as it was left. Falls back to plain state if storage is unavailable.
 */
export function usePersistedState<T>(key: string, initial: T | (() => T), override?: T | null): [T, React.Dispatch<React.SetStateAction<T>>] {
  const storageKey = `ui-state:v1:${key}`;
  const [value, setValue] = useState<T>(() => {
    if (override !== undefined && override !== null) return override; // e.g. a link that carries its own filter in the URL
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw !== null) return JSON.parse(raw) as T;
    } catch {
      // unreadable or blocked — start from the default
    }
    return typeof initial === "function" ? (initial as () => T)() : initial;
  });
  const set = useCallback<React.Dispatch<React.SetStateAction<T>>>(
    (next) => {
      setValue((prev) => {
        const resolved = typeof next === "function" ? (next as (p: T) => T)(prev) : next;
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(resolved));
        } catch {
          // storage full or blocked — keep working in memory
        }
        return resolved;
      });
    },
    [storageKey]
  );
  return [value, set];
}
